import {
  useDialogState,
  Dialog,
  DialogBackdrop
} from 'reakit/Dialog'
import { Button } from '@zuto/atoms'
import styled from 'styled-components'
import { LargeAlert } from '@zuto/icons'
import React, { useRef, useEffect } from 'react'
import { textStyle } from '@zuto/utilities'
import {
  color, compose
} from 'styled-system'
import Box from '@zuto/core/src/box'

const StyledDialog = styled(Dialog)`
  margin: auto 64px;
  border-radius: 12px;
  background: #FFFFFF;
  box-shadow: 0px 5px 28px rgba(166, 175, 185, 0.3);
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
  padding: 24px 16px;
  position: fixed;
  top: 50%;
  left: calc(50% - 64px);
  transform: translate(-50%, -50%);
  max-width: 364px;
`

const StyledBackdrop = styled(DialogBackdrop)`
background-color: rgb(0 0 0 / 50%);
width: 100vw;
height: 100vh;
position: fixed;
left: 0;
top: 0;
display: flex;
z-index: 999;
`

const StyledButton = styled(Button)`
justify-content: center;
min-width: 221px;
`

const StyledBox = styled(Box)`
margin-bottom: 24px;
justify-content: center;
`

const StyledHeading = styled(Box)`
margin: 16px;
justify-content: center;
${(props) => compose(color, textStyle(props))({
    ...props,
    textStyle: 'body.regular.medium',
    color: 'neutrals.4'
  })}
`

interface ErrorMessageProps {
  titleText?: string
  ctaText?: string
  children: React.ReactNode
  onClick?: () => void
}

const ErrorModal = ({
  titleText, ctaText, children, onClick
}: ErrorMessageProps): JSX.Element => {
  const dialog = useDialogState({ visible: true, baseId: 'error-message' })

  const ref = useRef<HTMLButtonElement>(null)

  useEffect(() => {
    if (dialog.visible && ref.current) {
      ref.current.focus()
    }
  }, [dialog.visible])

  return (
    <>
      <StyledBackdrop {...dialog}>
        <StyledDialog
          {...dialog}
          data-testid="error-modal"
          hideOnClickOutside={false}
          preventBodyScroll
          aria-labelledby="error-title"
          aria-describedby="error-text"
        >
          <LargeAlert />
          <StyledHeading id="error-title">
            {titleText || 'Oh, snap!'}
          </StyledHeading>
          <StyledBox id="error-text" color="neutrals.3" textStyle="body.current.regular">
            {children}
          </StyledBox>
          {ctaText && (
            <StyledButton
              ref={ref}
              data-testid="error-cta"
              variant="secondary"
              onClick={() => {
                if (onClick) {
                  onClick()
                } else {
                  dialog.hide()
                }
              }}
            >
            {ctaText || 'Enter address'}
            </StyledButton>
          )}
        </StyledDialog>
      </StyledBackdrop>
    </>
  )
}

export default ErrorModal
